import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateDataManagementFile } from "@/hooks/useCreateDataManagementFile";
import { useOrg } from "@/hooks/useOrg";
import { DataManagementFile } from "@/hooks/useDataManagementFiles";

export const useDataManagementFileOperations = (refetch: () => void) => {
  const { t } = useTranslation();
  const { org } = useOrg();

  const [
    createFile,
    { loading: uploadLoading, data: uploadData, error: uploadError },
  ] = useCreateDataManagementFile();

  useEffect(() => {
    if (uploadData?.id && !uploadLoading) {
      toast.success(t("dataManagement.upload.success.title"), {
        description: t("dataManagement.upload.success.message"),
        action: {
          label: t("dataManagement.upload.success.close"),
          onClick: () => toast.dismiss(),
        },
      });
      refetch();
    }
  }, [uploadData?.id, uploadLoading, t, refetch]);

  useEffect(() => {
    if (uploadError) {
      toast.error(t("dataManagement.upload.error.title"), {
        description: t("dataManagement.upload.error.message"),
        action: {
          label: t("dataManagement.upload.error.close"),
          onClick: () => toast.dismiss(),
        },
      });
    }
  }, [uploadError, t]);

  const handleDownload = (file: DataManagementFile) => {
    if (file.url) {
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const uploadFile = (file: File) => {
    if (org?.id) {
      createFile({
        organizationId: org.id,
        file,
      });
    }
  };

  return {
    uploadLoading,
    handleDownload,
    uploadFile,
  };
};
